import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import {User, Package, Heart, MapPin, Settings, LogOut, ChevronRight, CreditCard, ShoppingCart, X, Receipt, RefreshCw} from 'lucide-react';
import { lumi } from '../lib/lumi';
import { useTheme } from '../contexts/ThemeContext';
import { Helmet } from 'react-helmet-async';
import AddressManager from '../components/AddressManager';

const MemberPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const [user, setUser] = useState(lumi.auth.user);
  const [isLoading, setIsLoading] = useState(true);
  const [orders, setOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [favorites, setFavorites] = useState<any[]>([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [customerData, setCustomerData] = useState<any>(null);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [editPhone, setEditPhone] = useState("");
  const [isSavingPhone, setIsSavingPhone] = useState(false);
  const [pointsTransactions, setPointsTransactions] = useState<any[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'zh';
  const pathParts = location.pathname.split('/');
  // V2 路由 /[lang]/member/v2/{tab}：tab 在 index 4；舊版 /[lang]/member/{tab}：tab 在 index 3
  const isV2 = pathParts[3] === 'v2';
  const currentTab = (isV2 ? pathParts[4] : pathParts[3]) || 'overview';
  // 側邊欄／站內連結需保留在當前世界（V2 ↔ 舊版），避免使用者跳出 V2
  const memberBase = isV2 ? `/${currentLang}/member/v2` : `/${currentLang}/member`;

  // Content translations
  const content = {
    zh: {
      title: '會員中心 | ONGU STUDIO',
      welcome: '歡迎回來，',
      memberLevel: '一般會員',
      tabs: {
        overview: '帳戶總覽',
        orders: '歷史訂單',
        favorites: '收藏清單',
        cart: '我的購物車',
        addresses: '常用地址',
        points: '點數交易記錄',
        subscriptions: '訂閱進度',
        settings: '帳戶設定',
        logout: '登出',
      },
      overview: {
        title: '帳戶總覽',
        recentOrders: '最近訂單',
        noOrders: '尚無訂單紀錄',
        viewAllOrders: '查看所有訂單',
        accountDetails: '會員資料',
        edit: '編輯',
      },
      orders: {
        title: '歷史訂單',
        orderId: '訂單編號',
        date: '日期',
        status: '狀態',
        shipping: '配送方式',
        total: '金額',
        action: '操作',
        view: '查看詳情',
        pending: '待付款',
        paid: '已付款',
        processing: '處理中',
        shipped: '已出貨',
        completed: '已完成',
        cancelled: '已取消',
        refunded: '已退款',
        detail: {
          title: '訂單詳情',
          items: '訂購商品',
          quantity: '數量',
          subtotal: '小計',
          shipping: '配送方式',
          shippingFee: '運費',
          recipient: '收件人資訊',
          name: '姓名',
          phone: '電話',
          email: '電子郵件',
          address: '地址',
          payment: '付款方式',
          orderTime: '下單時間',
          close: '關閉',
        },
      },
      common: {
        loading: '載入中...',
        loginRequired: '請先登入會員',
        login: '登入',
      }
    },
    en: {
      title: 'Member Center | ONGU STUDIO',
      welcome: 'Welcome back,',
      memberLevel: 'Member',
      tabs: {
        overview: 'Overview',
        orders: 'Orders',
        favorites: 'Wishlist',
        cart: 'My Cart',
        addresses: 'Addresses',
        points: 'Points History',
        subscriptions: 'Subscriptions',
        settings: 'Settings',
        logout: 'Logout',
      },
      overview: {
        title: 'Account Overview',
        recentOrders: 'Recent Orders',
        noOrders: 'No recent orders',
        viewAllOrders: 'View All Orders',
        accountDetails: 'Account Details',
        edit: 'Edit',
      },
      orders: {
        title: 'Order History',
        orderId: 'Order ID',
        date: 'Date',
        status: 'Status',
        shipping: 'Shipping',
        total: 'Total',
        action: 'Action',
        view: 'View Details',
        processing: 'Processing',
        completed: 'Completed',
        cancelled: 'Cancelled',
        pending: 'Pending',
        detail: {
          title: 'Order Details',
          items: 'Items',
          quantity: 'Qty',
          subtotal: 'Subtotal',
          shipping: 'Shipping Method',
          shippingFee: 'Shipping Fee',
          recipient: 'Recipient Information',
          name: 'Name',
          phone: 'Phone',
          email: 'Email',
          address: 'Address',
          payment: 'Payment Method',
          orderTime: 'Order Time',
          close: 'Close',
        },
      },
      common: {
        loading: 'Loading...',
        loginRequired: 'Please login first',
        login: 'Login',
      }
    }
  };

  const t = content[currentLang];

  // Payment method display names mapping
  const paymentMethodNames: Record<string, string> = {
    credit: '信用卡',
    atm: 'ATM轉帳',
    convenience: '超商付款',
    linepay: 'LINE Pay',
    transfer: 'ATM轉帳',
    cash: '超商付款',
    creditCard: '信用卡',
    net30: '月結 30 天',
    newebpay: '信用卡'
  };

  // Listen for auth changes
  useEffect(() => {
    // 初始化完成，設置載入狀態為 false
    setIsLoading(false);
    
    const unsubscribe = lumi.auth.onAuthChange((authResult) => {
      setUser(authResult.user);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = async () => {
    try {
      await lumi.auth.signIn();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleLogout = () => {
    lumi.auth.signOut();
    navigate(`/${currentLang}`);
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const ordersPerPage = 10;

  // Fetch user's real orders from database with pagination
  useEffect(() => {
    const fetchOrders = async () => {
      if (!user?.email) return;
      
      setOrdersLoading(true);
      try {
        const { list, total } = await lumi.entities.orders.list({
          filter: { customerEmail: user.email },
          sort: { createdAt: -1 },
          limit: ordersPerPage,
          skip: (currentPage - 1) * ordersPerPage
        });
        console.log('📦 Fetched orders:', { total, count: list?.length, page: currentPage });
        setOrders(list || []);
        setTotalOrders(total || 0);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
        setOrders([]);
        setTotalOrders(0);
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [user?.email, currentPage]);

  // Fetch user's favorites with product details
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!user?.email) return;
      
      setFavoritesLoading(true);
      try {
        const { list } = await lumi.entities.favorites.list({
          filter: { userEmail: user.email },
          sort: { createdAt: -1 },
          limit: 100
        });
        
        // Fetch product details for each favorite
        const favoritesWithProducts = await Promise.all(
          (list || []).map(async (fav) => {
            try {
              const product = await lumi.entities.products.get(fav.productId);
              return { ...fav, product };
            } catch (error) {
              console.error('Failed to fetch product:', error);
              return { ...fav, product: null };
            }
          })
        );
        
        setFavorites(favoritesWithProducts.filter(f => f.product));
      } catch (error) {
        console.error('Failed to fetch favorites:', error);
        setFavorites([]);
      } finally {
        setFavoritesLoading(false);
      }
    };

    fetchFavorites();
  }, [user?.email]);

  // Fetch customer data from customers collection
  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!user?.email) return;
      
      try {
        const { list } = await lumi.entities.customers.list({
          filter: { email: user.email },
          limit: 1
        });
        
        if (list && list.length > 0) {
          setCustomerData(list[0]);
          setEditPhone(list[0].phone || "");
        }
      } catch (error) {
        console.error('Failed to fetch customer data:', error);
      }
    };

    fetchCustomerData();
  }, [user?.email]);

  // Fetch points transactions
  useEffect(() => {
    const fetchPointsTransactions = async () => {
      if (!user?.email) return;
      
      setTransactionsLoading(true);
      try {
        const { list } = await lumi.entities.points_transactions.list({
          filter: { customerEmail: user.email },
          sort: { createdAt: -1 },
          limit: 100
        });
        setPointsTransactions(list || []);
      } catch (error) {
        console.error('Failed to fetch points transactions:', error);
        setPointsTransactions([]);
      } finally {
        setTransactionsLoading(false);
      }
    };

    fetchPointsTransactions();
  }, [user?.email]);

  // Fetch subscriptions
  useEffect(() => {
    const fetchSubscriptions = async () => {
      if (!user?.email) return;
      setSubscriptionsLoading(true);
      try {
        const { list } = await lumi.entities.subscriptions.list({
          filter: { customerEmail: user.email },
          sort: { createdAt: -1 },
          limit: 50
        });
        setSubscriptions(list || []);
      } catch (error) {
        console.error('Failed to fetch subscriptions:', error);
        setSubscriptions([]);
      } finally {
        setSubscriptionsLoading(false);
      }
    };
    fetchSubscriptions();
  }, [user?.email]);

  // Remove from favorites
  const handleRemoveFavorite = async (favoriteId: string) => {
    try {
      await lumi.entities.favorites.delete(favoriteId);
      setFavorites(favorites.filter(f => f._id !== favoriteId));
      window.dispatchEvent(new Event('favoriteChanged'));
    } catch (error) {
      console.error('Failed to remove favorite:', error);
    }
  };

  // Save phone number
  const handleSavePhone = async () => {
    if (!customerData?._id) {
      alert(currentLang === 'zh' ? '無法找到會員資料' : 'Customer data not found');
      return;
    }

    setIsSavingPhone(true);
    try {
      await lumi.entities.customers.update(customerData._id, {
        phone: editPhone
      });

      setCustomerData({ ...customerData, phone: editPhone });
      setIsEditingPhone(false);
      alert(currentLang === 'zh' ? '✅ 手機號碼已更新' : '✅ Phone number updated');
    } catch (error) {
      console.error('Failed to save phone:', error);
      alert(currentLang === 'zh' ? '❌ 保存失敗' : '❌ Save failed');
    } finally {
      setIsSavingPhone(false);
    }
  };

  // 顯示載入中狀態，避免首次渲染白屏
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black pt-24 pb-12 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white"></div>
          <p className="mt-4 text-gray-500 dark:text-gray-400 text-sm">{t.common.loading}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white dark:bg-gray-900 px-4">
        <Helmet>
          <title>{t.title}</title>
        </Helmet>
        <div className="text-center">
          <h2 className="text-2xl font-light mb-4 text-gray-900 dark:text-gray-100">{t.common.loginRequired}</h2>
          <button
            onClick={handleLogin}
            className="px-8 py-3 bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors duration-300 tracking-widest text-sm"
          >
            {t.common.login}
          </button>
        </div>
      </div>
    );
  }

  const renderSidebar = () => (
    <div className="w-full lg:w-80 flex-shrink-0 space-y-8">
      {/* Member Info Card */}
      <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-6 relative overflow-hidden group">
        <div className="absolute top-0 left-0 w-1 h-full bg-black dark:bg-gray-200 transform origin-bottom scale-y-0 group-hover:scale-y-100 transition-transform duration-300"></div>
        
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center text-2xl text-gray-500 dark:text-gray-400 font-light">
             {user.picture ? (
                <img src={user.picture} alt={user.userName || 'User'} className="w-full h-full object-cover rounded-full" />
             ) : (
                (user.userName || 'U').charAt(0).toUpperCase()
             )}
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{user.userName || 'Member'}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-light tracking-wide">{t.memberLevel}</p>
          </div>
        </div>
        
        <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
           <p className="text-xs text-gray-400 break-all font-mono">{user.email}</p>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex flex-col space-y-1">
        {[
          { id: 'overview', icon: User, label: t.tabs.overview, href: `${memberBase}/overview` },
          { id: 'orders', icon: Package, label: t.tabs.orders, href: `${memberBase}/orders` },
          { id: 'subscriptions', icon: RefreshCw, label: t.tabs.subscriptions, href: `${memberBase}/subscriptions` },
          { id: 'points', icon: Receipt, label: t.tabs.points, href: `${memberBase}/points` },
          { id: 'wishlist', icon: Heart, label: t.tabs.favorites, href: `${memberBase}/wishlist` },
          { id: 'cart', icon: ShoppingCart, label: t.tabs.cart, href: isV2 ? `/${currentLang}/cart/v2` : `/${currentLang}/cart` },
          { id: 'addresses', icon: MapPin, label: t.tabs.addresses, href: `${memberBase}/addresses` },
          { id: 'settings', icon: Settings, label: t.tabs.settings, href: `${memberBase}/settings` },
        ].map((item) => {
          const isActive = currentTab === item.id;
          return (
            <Link
              key={item.id}
              to={item.href}
              className={`flex items-center justify-between px-6 py-4 transition-all duration-200 border-l-2 ${
                isActive
                  ? 'bg-gray-50 dark:bg-gray-900 border-black dark:border-white text-black dark:text-white'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <div className="flex items-center space-x-3">
                <item.icon size={18} strokeWidth={1.5} />
                <span className="text-sm tracking-wide">{item.label}</span>
              </div>
              <ChevronRight size={16} className={`transition-transform duration-200 ${isActive ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`} />
            </Link>
          );
        })}
        
        <button
          onClick={handleLogout}
          className="flex items-center justify-between px-6 py-4 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 hover:text-red-600 transition-all duration-200 border-l-2 border-transparent mt-8"
        >
          <div className="flex items-center space-x-3">
            <LogOut size={18} strokeWidth={1.5} />
            <span className="text-sm tracking-wide">{t.tabs.logout}</span>
          </div>
        </button>
      </nav>
    </div>
  );

  const renderContent = () => {
    switch (currentTab) {
      case 'overview':
        return (
          <div className="space-y-8 animate-fade-in">
            <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-light tracking-wide text-gray-900 dark:text-gray-100">{t.overview.title}</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                 <div className="bg-gray-50 dark:bg-gray-900 p-6 border border-gray-100 dark:border-gray-800">
                    <div className="text-gray-500 text-xs uppercase tracking-widest mb-2">Wallet Points</div>
                    <div className="text-3xl font-light text-gray-900 dark:text-gray-100">{customerData?.points?.toLocaleString() || 0}</div>
                 </div>
                 <div className="bg-gray-50 dark:bg-gray-900 p-6 border border-gray-100 dark:border-gray-800">
                    <div className="text-gray-500 text-xs uppercase tracking-widest mb-2">Coupons</div>
                    <div className="text-3xl font-light text-gray-900 dark:text-gray-100">0</div>
                 </div>
                 <div className="bg-gray-50 dark:bg-gray-900 p-6 border border-gray-100 dark:border-gray-800">
                    <div className="text-gray-500 text-xs uppercase tracking-widest mb-2">Level</div>
                    <div className="text-xl font-light text-gray-900 dark:text-gray-100">{t.memberLevel}</div>
                 </div>
              </div>

              <div className="border-t border-gray-100 dark:border-gray-800 pt-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-normal text-gray-900 dark:text-gray-100">{t.overview.recentOrders}</h3>
                  <Link 
                    to={`${memberBase}/orders`}
                    className="text-xs text-gray-500 hover:text-black dark:hover:text-white transition-colors border-b border-gray-300 pb-0.5"
                  >
                    {t.overview.viewAllOrders}
                  </Link>
                </div>
                
                {ordersLoading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
                  </div>
                ) : orders.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th className="px-4 py-3 font-medium">{t.orders.orderId}</th>
                          <th className="px-4 py-3 font-medium">{t.orders.date}</th>
                          <th className="px-4 py-3 font-medium">{t.orders.status}</th>
                          <th className="px-4 py-3 font-medium text-right">{t.orders.total}</th>
                          <th className="px-4 py-3 font-medium text-right">{t.orders.action}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <tr key={order.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                            <td className="px-4 py-4 font-medium text-gray-900 dark:text-gray-100">{order.orderNumber || order._id}</td>
                            <td className="px-4 py-4 text-gray-500">{new Date(order.createdAt).toLocaleDateString('zh-TW')}</td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                order.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                order.status === 'paid' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                                order.status === 'processing' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                                order.status === 'shipped' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
                                order.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                order.status === 'refunded' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' :
                                order.status === 'cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {t.orders[order.status as keyof typeof t.orders]}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-right font-medium">NT$ {order.totalAmount || 0}</td>
                            <td className="px-4 py-4 text-right">
                              <button 
                                onClick={() => {
                                  setSelectedOrder(order);
                                  setShowOrderDetail(true);
                                }}
                                className="text-sm text-gray-500 hover:text-black dark:hover:text-white transition-colors border-b border-gray-300 pb-0.5"
                              >
                                {t.orders.view}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm py-4">{t.overview.noOrders}</p>
                )}
              </div>
            </div>
          </div>
        );
      
      case 'subscriptions':
        return (
          <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-8 animate-fade-in">
            <h2 className="text-2xl font-light tracking-wide text-gray-900 dark:text-gray-100 mb-6">
              {currentLang === 'zh' ? '訂閱進度' : 'Subscription Progress'}
            </h2>
            {subscriptionsLoading ? (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
              </div>
            ) : subscriptions.length > 0 ? (
              <div className="space-y-5">
                {subscriptions.map((sub: any) => {
                  const pct = Math.min(Math.round((sub.deliveredMonths / sub.totalMonths) * 100), 100);
                  const statusMap: Record<string, { label: string; cls: string }> = {
                    active: { label: currentLang === 'zh' ? '進行中' : 'Active', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
                    paused: { label: currentLang === 'zh' ? '暫停' : 'Paused', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
                    completed: { label: currentLang === 'zh' ? '已完成' : 'Completed', cls: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400' },
                  };
                  const st = statusMap[sub.status] || statusMap.active;
                  return (
                    <div key={sub._id} className="border border-gray-200 dark:border-gray-800 p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="text-sm font-light text-gray-900 dark:text-gray-100 mb-1">
                            {sub.productName || sub.productId}
                          </div>
                          <div className="text-xs text-gray-500 font-light">
                            {currentLang === 'zh' ? `每月 ${sub.deliveryDay} 日出貨` : `Ships on ${sub.deliveryDay}th monthly`}
                            {' · '} NT$ {sub.pricePerMonth?.toLocaleString()} / {currentLang === 'zh' ? '月' : 'mo'}
                          </div>
                        </div>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-light ${st.cls}`}>
                          {st.label}
                        </span>
                      </div>

                      {/* 進度條 */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500">
                            {currentLang === 'zh' ? '出貨進度' : 'Delivery Progress'}
                          </span>
                          <span className="text-xs font-light text-gray-900 dark:text-gray-100">
                            {sub.deliveredMonths} / {sub.totalMonths} {currentLang === 'zh' ? '個月' : 'months'}
                          </span>
                        </div>
                        <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              sub.status === 'completed' ? 'bg-gray-400' :
                              sub.status === 'paused' ? 'bg-amber-400' : 'bg-emerald-500'
                            }`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-xs font-light">
                        <div>
                          <span className="text-gray-500">{currentLang === 'zh' ? '下次出貨' : 'Next Ship'}:</span>
                          <span className="ml-2 text-gray-900 dark:text-gray-100">
                            {sub.status === 'completed' ? '—' : new Date(sub.nextDeliveryDate).toLocaleDateString(currentLang === 'zh' ? 'zh-TW' : 'en-US')}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">{currentLang === 'zh' ? '訂閱開始' : 'Started'}:</span>
                          <span className="ml-2 text-gray-900 dark:text-gray-100">
                            {new Date(sub.startDate).toLocaleDateString(currentLang === 'zh' ? 'zh-TW' : 'en-US')}
                          </span>
                        </div>
                      </div>

                      {sub.status === 'paused' && sub.pauseReason && (
                        <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400 font-light rounded-sm">
                          {currentLang === 'zh' ? '暫停原因：' : 'Pause reason: '}{sub.pauseReason}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20 text-gray-400 dark:text-gray-600 font-light">
                <RefreshCw size={48} className="mx-auto mb-4 opacity-20" />
                <p>{currentLang === 'zh' ? '尚無訂閱記錄' : 'No subscriptions yet'}</p>
                <a
                  href={`/${currentLang}/shop`}
                  className="inline-block mt-6 px-6 py-2 bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors text-sm tracking-widest"
                >
                  {currentLang === 'zh' ? '前往購物' : 'Go Shopping'}
                </a>
              </div>
            )}
          </div>
        );

      case 'orders':
        const totalPages = Math.ceil(totalOrders / ordersPerPage);
        return (
          <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-8 animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-light tracking-wide text-gray-900 dark:text-gray-100">{t.orders.title}</h2>
              {totalOrders > 0 && (
                <div className="text-sm text-gray-500">
                  {currentLang === 'zh' ? `共 ${totalOrders} 筆訂單` : `${totalOrders} orders total`}
                </div>
              )}
            </div>
            
            {ordersLoading ? (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
              </div>
            ) : orders.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
                      <tr>
                        <th className="px-6 py-4 text-left font-medium">{t.orders.orderId}</th>
                        <th className="px-6 py-4 text-left font-medium">{t.orders.date}</th>
                        <th className="px-6 py-4 text-left font-medium">{t.orders.status}</th>
                        <th className="px-6 py-4 text-left font-medium">{t.orders.shipping}</th>
                        <th className="px-6 py-4 text-right font-medium">{t.orders.total}</th>
                        <th className="px-6 py-4 text-right font-medium">{t.orders.action}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order._id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                          <td className="px-6 py-4 font-medium text-gray-900 dark:text-gray-100">{order.orderNumber || order._id}</td>
                          <td className="px-6 py-4 text-gray-500">{new Date(order.createdAt).toLocaleDateString(currentLang === 'zh' ? 'zh-TW' : 'en-US')}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              order.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                              order.status === 'paid' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                              order.status === 'processing' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                              order.status === 'shipped' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
                              order.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                              order.status === 'refunded' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' :
                              order.status === 'cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                            }`}>
                              {t.orders[order.status as keyof typeof t.orders] || order.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">{order.shippingMethod || '宅配到府'}</td>
                          <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-gray-100">NT$ {order.totalAmount?.toLocaleString() || 0}</td>
                          <td className="px-6 py-4 text-right">
                            <button 
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowOrderDetail(true);
                              }}
                              className="text-sm text-gray-500 hover:text-black dark:hover:text-white transition-colors border-b border-gray-300 pb-0.5"
                            >
                              {t.orders.view}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8 pt-6 border-t border-gray-100 dark:border-gray-800">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {currentLang === 'zh' ? '上一頁' : 'Previous'}
                    </button>
                    
                    <div className="flex items-center gap-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-4 py-2 text-sm border transition-colors ${
                            currentPage === page
                              ? 'bg-black dark:bg-white text-white dark:text-black border-black dark:border-white'
                              : 'border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 text-sm border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {currentLang === 'zh' ? '下一頁' : 'Next'}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20 text-gray-400 dark:text-gray-600 font-light">
                <Package size={48} className="mx-auto mb-4 opacity-20" />
                <p>{t.overview.noOrders}</p>
              </div>
            )}
          </div>
        );

      case 'wishlist':
        return (
          <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-8 animate-fade-in">
            <h2 className="text-2xl font-light tracking-wide text-gray-900 dark:text-gray-100 mb-6">{t.tabs.favorites}</h2>
            
            {favoritesLoading ? (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
              </div>
            ) : favorites.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favorites.map((favorite) => (
                  <div key={favorite._id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 overflow-hidden group">
                    {/* Product Image */}
                    <Link to={`/${currentLang}/shop/${favorite.product.category}/${favorite.product._id}/${favorite.product.slug || 'product'}`} className="block relative aspect-square overflow-hidden bg-gray-100 dark:bg-gray-800">
                      <img
                        src={favorite.product.images?.[0] || 'https://via.placeholder.com/400'}
                        alt={currentLang === 'zh' ? favorite.product.name : favorite.product.nameEn}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </Link>
                    
                    {/* Product Info */}
                    <div className="p-4">
                      <Link to={`/${currentLang}/shop/${favorite.product.category}/${favorite.product._id}/${favorite.product.slug || 'product'}`}>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 line-clamp-2 hover:text-gray-600 dark:hover:text-gray-400 transition-colors">
                          {currentLang === 'zh' ? favorite.product.name : favorite.product.nameEn}
                        </h3>
                      </Link>
                      <div className="flex items-center justify-between mt-4">
                        <span className="text-lg font-medium text-gray-900 dark:text-gray-100">NT$ {favorite.product.price?.toLocaleString()}</span>
                        <button
                          onClick={() => handleRemoveFavorite(favorite._id)}
                          className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 text-xs border-b border-red-300 pb-0.5 transition-colors"
                        >
                          {currentLang === 'zh' ? '移除' : 'Remove'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 text-gray-400 dark:text-gray-600 font-light">
                <Heart size={48} className="mx-auto mb-4 opacity-20" />
                <p>{currentLang === 'zh' ? '尚無收藏商品' : 'No favorite products yet'}</p>
                <Link
                  to={`/${currentLang}/shop`}
                  className="inline-block mt-6 px-6 py-2 bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors text-sm tracking-widest"
                >
                  {currentLang === 'zh' ? '前往購物' : 'Go Shopping'}
                </Link>
              </div>
            )}
          </div>
        );

      case 'addresses':
        return <AddressManager currentLang={currentLang} />;
      
      case 'points':
        return (
          <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-8 animate-fade-in">
            <h2 className="text-2xl font-light tracking-wide text-gray-900 dark:text-gray-100 mb-6">
              {currentLang === 'zh' ? '點數交易記錄' : 'Points Transaction History'}
            </h2>
            
            {transactionsLoading ? (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
              </div>
            ) : pointsTransactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
                    <tr>
                      <th className="px-6 py-4 text-left font-medium">{currentLang === 'zh' ? '時間' : 'Time'}</th>
                      <th className="px-6 py-4 text-left font-medium">{currentLang === 'zh' ? '類型' : 'Type'}</th>
                      <th className="px-6 py-4 text-right font-medium">{currentLang === 'zh' ? '點數變動' : 'Points'}</th>
                      <th className="px-6 py-4 text-right font-medium">{currentLang === 'zh' ? '餘額' : 'Balance'}</th>
                      <th className="px-6 py-4 text-left font-medium">{currentLang === 'zh' ? '說明' : 'Description'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pointsTransactions.map((transaction) => (
                      <tr key={transaction._id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                        <td className="px-6 py-4 text-gray-500">
                          {new Date(transaction.createdAt).toLocaleString(currentLang === 'zh' ? 'zh-TW' : 'en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            transaction.type === 'earned' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                            transaction.type === 'used' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                            transaction.type === 'refund' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                            transaction.type === 'adjustment' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                          }`}>
                            {transaction.type === 'earned' ? (currentLang === 'zh' ? '獲得' : 'Earned') :
                             transaction.type === 'used' ? (currentLang === 'zh' ? '使用' : 'Used') :
                             transaction.type === 'refund' ? (currentLang === 'zh' ? '退款' : 'Refund') :
                             transaction.type === 'adjustment' ? (currentLang === 'zh' ? '調整' : 'Adjustment') :
                             transaction.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right font-medium">
                          <span className={transaction.points >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                            {transaction.points >= 0 ? '+' : ''}{transaction.points?.toLocaleString() || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100 font-medium">
                          {transaction.balanceAfter?.toLocaleString() || 0}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                          {transaction.description || (transaction.orderNumber ? `${currentLang === 'zh' ? '訂單' : 'Order'} ${transaction.orderNumber}` : '-')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-20 text-gray-400 dark:text-gray-600 font-light">
                <Receipt size={48} className="mx-auto mb-4 opacity-20" />
                <p>{currentLang === 'zh' ? '尚無點數交易記錄' : 'No points transactions yet'}</p>
              </div>
            )}
          </div>
        );
        
      case 'settings':
        return (
          <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-8 animate-fade-in">
            <h2 className="text-2xl font-light tracking-wide text-gray-900 dark:text-gray-100 mb-8">
              {currentLang === 'zh' ? '帳戶設定' : 'Account Settings'}
            </h2>
            
            {/* Personal Information Section */}
            <div className="space-y-8">
              <div className="pb-8 border-b border-gray-100 dark:border-gray-800">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-6 uppercase tracking-widest">
                  {currentLang === 'zh' ? '個人資訊' : 'Personal Information'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Profile Picture */}
                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-500 uppercase tracking-widest mb-3">
                      {currentLang === 'zh' ? '頭像' : 'Profile Picture'}
                    </label>
                    <div className="flex items-center space-x-6">
                      <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center text-3xl text-gray-500 dark:text-gray-400 font-light overflow-hidden">
                        {user.picture ? (
                          <img src={user.picture} alt={user.userName || 'User'} className="w-full h-full object-cover" />
                        ) : (
                          (user.userName || 'U').charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className="text-sm text-gray-400">
                        {currentLang === 'zh' ? '頭像由 Lumi 平台管理' : 'Profile picture managed by Lumi platform'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Username */}
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-widest mb-3">
                      {currentLang === 'zh' ? '用戶名稱' : 'Username'}
                    </label>
                    <input
                      type="text"
                      value={user.userName || ''}
                      disabled
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none cursor-not-allowed"
                    />
                  </div>
                  
                  {/* Email */}
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-widest mb-3">
                      {currentLang === 'zh' ? '電子郵件' : 'Email'}
                    </label>
                    <input
                      type="email"
                      value={user.email || ''}
                      disabled
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none cursor-not-allowed"
                    />
                  </div>
                  
                  {/* User ID */}
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-widest mb-3">
                      {currentLang === 'zh' ? '用戶 ID' : 'User ID'}
                    </label>
                    <input
                      type="text"
                      value={user.userId || ''}
                      disabled
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-400 dark:text-gray-500 text-sm font-mono focus:outline-none cursor-not-allowed"
                    />
                  </div>
                  
                  {/* Phone Number */}
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-widest mb-3">
                      {currentLang === 'zh' ? '手機號碼' : 'Phone Number'}
                    </label>
                    <div className="flex items-center space-x-3">
                      {isEditingPhone ? (
                        <>
                          <input
                            type="tel"
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.target.value)}
                            placeholder={currentLang === 'zh' ? '請輸入手機號碼' : 'Enter phone number'}
                            className="flex-1 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:border-black dark:focus:border-white transition-colors"
                          />
                          <button
                            onClick={handleSavePhone}
                            disabled={isSavingPhone}
                            className="px-4 py-3 bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors text-sm disabled:opacity-50 whitespace-nowrap"
                          >
                            {isSavingPhone ? (currentLang === 'zh' ? '保存中...' : 'Saving...') : (currentLang === 'zh' ? '保存' : 'Save')}
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingPhone(false);
                              setEditPhone(customerData?.phone || "");
                            }}
                            disabled={isSavingPhone}
                            className="px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm disabled:opacity-50 whitespace-nowrap"
                          >
                            {currentLang === 'zh' ? '取消' : 'Cancel'}
                          </button>
                        </>
                      ) : (
                        <>
                          <input
                            type="tel"
                            value={customerData?.phone || ''}
                            disabled
                            placeholder={currentLang === 'zh' ? '未設定' : 'Not set'}
                            className="flex-1 px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none cursor-not-allowed"
                          />
                          <button
                            onClick={() => setIsEditingPhone(true)}
                            className="px-4 py-3 bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors text-sm whitespace-nowrap"
                          >
                            {currentLang === 'zh' ? '編輯' : 'Edit'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  
                </div>
              </div>
              
              {/* Account Statistics */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-6 uppercase tracking-widest">
                  {currentLang === 'zh' ? '帳戶統計' : 'Account Statistics'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gray-50 dark:bg-gray-900 p-6 border border-gray-100 dark:border-gray-800">
                    <div className="text-gray-500 text-xs uppercase tracking-widest mb-2">
                      {currentLang === 'zh' ? '會員等級' : 'Member Level'}
                    </div>
                    <div className="text-2xl font-light text-gray-900 dark:text-gray-100">{t.memberLevel}</div>
                  </div>
                  
                  <Link 
                    to={`${memberBase}/orders`}
                    className="bg-gray-50 dark:bg-gray-900 p-6 border border-gray-100 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer group"
                  >
                    <div className="text-gray-500 text-xs uppercase tracking-widest mb-2 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">
                      {currentLang === 'zh' ? '訂單總數' : 'Total Orders'}
                    </div>
                    <div className="text-2xl font-light text-gray-900 dark:text-gray-100">{orders.length}</div>
                  </Link>
                  
                  <Link 
                    to={`${memberBase}/wishlist`}
                    className="bg-gray-50 dark:bg-gray-900 p-6 border border-gray-100 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer group"
                  >
                    <div className="text-gray-500 text-xs uppercase tracking-widest mb-2 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">
                      {currentLang === 'zh' ? '收藏商品' : 'Favorite Items'}
                    </div>
                    <div className="text-2xl font-light text-gray-900 dark:text-gray-100">{favorites.length}</div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        );
        
      default:
        return (
          <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-8 animate-fade-in flex items-center justify-center min-h-[400px]">
            <div className="text-center text-gray-400">
                <Settings size={48} className="mx-auto mb-4 opacity-20" strokeWidth={1} />
                <p className="font-light">Functionality coming soon...</p>
            </div>
          </div>
        );
    }
  };

  // Redirect to overview if no tab specified (after all hooks)
  // V2: /[lang]/member/v2 → /[lang]/member/v2/overview
  // 舊版: /[lang]/member → /[lang]/member/overview
  if (isV2) {
    if (pathParts.length <= 4 || !pathParts[4]) {
      return <Navigate to={`${memberBase}/overview`} replace />;
    }
  } else if (pathParts.length <= 3 || !pathParts[3]) {
    return <Navigate to={`${memberBase}/overview`} replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black pt-24 pb-12 transition-colors duration-300">
      <Helmet>
        <title>{t.title}</title>
      </Helmet>
      
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar */}
          {renderSidebar()}
          
          {/* Right Content */}
          <div className="flex-1">
             {renderContent()}
          </div>
        </div>
      </div>

      {/* Order Detail Modal */}
      {showOrderDetail && selectedOrder && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" onClick={() => setShowOrderDetail(false)}>
          <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 px-8 py-6 flex items-center justify-between">
              <h3 className="text-2xl font-light tracking-wide text-gray-900 dark:text-gray-100">{t.orders.detail.title}</h3>
              <button onClick={() => setShowOrderDetail(false)} className="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>

            {/* Content */}
            <div className="px-8 py-6 space-y-8">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4 pb-6 border-b border-gray-100 dark:border-gray-800">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">{t.orders.orderId}</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedOrder.orderNumber || selectedOrder._id}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">{t.orders.detail.orderTime}</div>
                  <div className="text-sm text-gray-900 dark:text-gray-100">{new Date(selectedOrder.createdAt).toLocaleString(currentLang === 'zh' ? 'zh-TW' : 'en-US')}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">{t.orders.status}</div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    selectedOrder.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                    selectedOrder.status === 'paid' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                    selectedOrder.status === 'processing' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                    selectedOrder.status === 'shipped' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
                    selectedOrder.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                    selectedOrder.status === 'refunded' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' :
                    selectedOrder.status === 'cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                  }`}>
                    {t.orders[selectedOrder.status as keyof typeof t.orders] || selectedOrder.status}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">{t.orders.total}</div>
                  <div className="text-lg font-medium text-gray-900 dark:text-gray-100">NT$ {selectedOrder.totalAmount?.toLocaleString() || 0}</div>
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4 uppercase tracking-widest">{t.orders.detail.items}</h4>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item: any, index: number) => (
                    <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
                      <div className="flex items-center space-x-4 flex-1">
                        {item.image && (
                          <img src={item.image} alt={item.productName} className="w-16 h-16 object-cover" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.productName}</div>
                          <div className="text-xs text-gray-500 mt-1">NT$ {item.price?.toLocaleString() || 0}</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">× {item.quantity}</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[100px] text-right">NT$ {((item.price || 0) * (item.quantity || 0)).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recipient Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-gray-100 dark:border-gray-800">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4 uppercase tracking-widest">{t.orders.detail.recipient}</h4>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-gray-500">{t.orders.detail.name}:</span> <span className="text-gray-900 dark:text-gray-100 ml-2">{selectedOrder.customerName || '-'}</span></div>
                    <div><span className="text-gray-500">{t.orders.detail.phone}:</span> <span className="text-gray-900 dark:text-gray-100 ml-2">{selectedOrder.customerPhone || '-'}</span></div>
                    <div><span className="text-gray-500">{t.orders.detail.email}:</span> <span className="text-gray-900 dark:text-gray-100 ml-2">{selectedOrder.customerEmail || '-'}</span></div>
                    {selectedOrder.shippingAddress?.fullAddress && (
                      <div><span className="text-gray-500">{t.orders.detail.address}:</span> <span className="text-gray-900 dark:text-gray-100 ml-2">{selectedOrder.shippingAddress.fullAddress}</span></div>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4 uppercase tracking-widest">{t.orders.detail.shipping}</h4>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-gray-500">{t.orders.detail.shipping}:</span> <span className="text-gray-900 dark:text-gray-100 ml-2">{selectedOrder.shippingMethod || '宅配'}</span></div>
                    {selectedOrder.shippingAddress?.storeName && (
                      <div><span className="text-gray-500">{currentLang === 'zh' ? '取貨門市' : 'Pickup Store'}:</span> <span className="text-gray-900 dark:text-gray-100 ml-2">{selectedOrder.shippingAddress.storeName}</span></div>
                    )}
                    {selectedOrder.shippingAddress?.storeAddress && (
                      <div><span className="text-gray-500">{currentLang === 'zh' ? '門市地址' : 'Store Address'}:</span> <span className="text-gray-900 dark:text-gray-100 ml-2">{selectedOrder.shippingAddress.storeAddress}</span></div>
                    )}
                    <div><span className="text-gray-500">{t.orders.detail.shippingFee}:</span> <span className="text-gray-900 dark:text-gray-100 ml-2">NT$ {selectedOrder.shippingFee || 0}</span></div>
                    {selectedOrder.couponCode && (
                      <div><span className="text-gray-500">{currentLang === 'zh' ? '優惠券代碼' : 'Coupon Code'}:</span> <span className="text-green-600 dark:text-green-400 ml-2 font-medium">{selectedOrder.couponCode}</span></div>
                    )}
                    {selectedOrder.discount > 0 && (
                      <div><span className="text-gray-500">{currentLang === 'zh' ? '優惠折扣' : 'Discount'}:</span> <span className="text-green-600 dark:text-green-400 ml-2 font-medium">- NT$ {selectedOrder.discount?.toLocaleString() || 0}</span></div>
                    )}
                    <div><span className="text-gray-500">{t.orders.detail.payment}:</span> <span className="text-gray-900 dark:text-gray-100 ml-2">{paymentMethodNames[selectedOrder.paymentMethod] || selectedOrder.paymentMethod || '線上付款'}</span></div>
                    {selectedOrder.notes && (
                      <div><span className="text-gray-500">{currentLang === 'zh' ? '備註' : 'Notes'}:</span> <span className="text-gray-900 dark:text-gray-100 ml-2">{selectedOrder.notes}</span></div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-8 py-6">
              <button 
                onClick={() => setShowOrderDetail(false)}
                className="w-full px-6 py-3 bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors duration-300 tracking-widest text-sm"
              >
                {t.orders.detail.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberPage;
